import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { OTHER } from '@constants/roomTypes';
import { subStylesDisplayNameById } from '@constants/subStyles';
import IDiscoverPage from '@models/Discover/i-discover-page';
import If from '@components/shared/conditional/If/If';
import { IRoomType } from '@models/Room/i-room-type';
import classNames from 'classnames';
import { useTheme } from '@components/theme';
import { useRouter } from 'next/router';
import styles from './styles.module.scss';
import NavSearch from '../NavSearch';
import SearchIcon from './assets/search-icon.svg';

ExploreHeader.propTypes = {
  roomType: PropTypes.number,
  styleType: PropTypes.number,
};

interface IExploreHeaderProps {
  discoverPage?: IDiscoverPage;
  roomType?: number;
  styleType?: number;
  hideSubHeader?: boolean;
  shouldDisplayForYou?: boolean;
  roomTypes: IRoomType[];
  selectedTab: number;
  navOpen: boolean;
  setNavOpen: (state: boolean) => void;
}

const MAX_DESCRIPTION_WORD_COUNT_TITLE = 11;
const MAX_DESCRIPTION_WORD_COUNT_SUBHEADER = 25;

export default function ExploreHeader({
  discoverPage,
  roomType = 0,
  styleType = 0,
  hideSubHeader = false,
  shouldDisplayForYou = false,
  roomTypes,
  selectedTab,
  navOpen,
  setNavOpen
}: IExploreHeaderProps) {
  const theme = useTheme();
  const [readMore, setReadMore] = useState(false);
  const roomTypeModel = roomTypes.find(({ id }) => Number(id) === roomType);
  const title = getHeader(roomTypeModel, styleType, discoverPage);
  const subHeader = getSubHeader(roomTypeModel, styleType, discoverPage);
  const readMoreTitleSplit = getReadMoreSplitText(title, MAX_DESCRIPTION_WORD_COUNT_TITLE);
  const readMoreSubHeaderSplit =
    getReadMoreSplitText(subHeader, MAX_DESCRIPTION_WORD_COUNT_SUBHEADER);
  const navSearchQueryInputRef = useRef<HTMLInputElement>(null);
  const [navSearchQueryText, setNavSearchQueryText] = useState('');

  const navSearchPlaceHolderText = 'Search inspiration like “modern bedroom”';
  const discoverTopicSelected = !!discoverPage?.name;
  const router = useRouter();

  useEffect(() => {
    if (navOpen) {
      navSearchQueryInputRef.current?.focus();
      document.body.style.position = 'fixed';
    } else {
      setNavSearchQueryText('');
      document.body.style.position = 'relative';
    }
  }, [navOpen]);

  return (
    <If condition={!hideSubHeader}>
      <div className={classNames(
        styles.ExploreHeader,
        { [styles.TopicSelected]: !discoverPage?.name },
      )}
      >
        <>
          <h1 className={classNames(theme.styles.Title2, styles.Header)}>
            {readMoreTitleSplit[0] ? (
              <>
                {readMoreTitleSplit[0]}
                <span className={classNames({ [styles.hideReadMoreText]: !readMore })}>
                  {readMoreTitleSplit[1]}
                </span>
                <button
                  type="button"
                  onClick={() => setReadMore(!readMore)}
                  className={styles.ReadMore}
                >
                  <strong>{`${readMore ? 'Read Less' : '... Read More'}`}</strong>
                </button>
              </>
            ) : (
              <>
                {boldTopicName(title)}
              </>
            )}
          </h1>
          <h2 className={classNames(
            theme.styles.Body,
            styles.SubHeader,
            styles.hideOnMobile
          )}
          >
            {readMoreSubHeaderSplit[0] ? (
              <>
                {readMoreSubHeaderSplit[0]}
                <span className={classNames({ [styles.hideReadMoreText]: !readMore })}>
                  {readMoreSubHeaderSplit[1]}
                </span>
                <button
                  type="button"
                  onClick={() => setReadMore(!readMore)}
                  className={styles.ReadMore}
                >
                  <strong>{`${readMore ? 'Read Less' : '... Read More'}`}</strong>
                </button>
              </>
            ) : (
              <>
                {subHeader}
              </>
            )}
          </h2>

          <If condition={!shouldDisplayForYou || selectedTab === 1}>
            <div
              className={classNames(
                styles.SearchBar,
                { [styles.NavSearchOpen]: navOpen },
                { [styles.DiscoverTopicSelected]: discoverTopicSelected }
              )}
            >
              <div className={styles.NavSearchContainer}>
                <div className={classNames(
                  styles.NavSearchBody,
                  { [styles.NavSearchOpen]: navOpen }
                )}
                >
                  <button
                    className={styles.SearchBarBackButton}
                    onClick={(() => router.push('/exp/interior-design-ideas'))}
                    aria-label="Back"
                    type="button"
                  >
                    <span className={`havenly-icon caret`} />
                  </button>

                  <button
                    className={classNames(
                      styles.SearchBarButton,
                      { [styles.NavSearchOpen]: navOpen }
                    )}
                    onClick={() => setNavOpen(!navOpen)}
                    type="button"
                  >
                    <If condition={!discoverTopicSelected && !navOpen}>
                      <span className={styles.hideOnDesktop}>
                        <SearchIcon />
                      </span>
                      <span className={styles.SearchBarCopy}>
                        {navSearchPlaceHolderText}
                      </span>
                      <span className={styles.hideOnMobile}>
                        <SearchIcon />
                      </span>
                    </If>
                    <If condition={discoverTopicSelected && !navOpen}>
                      <span className={styles.SearchBarCopyWithTopic}>
                        {discoverPage?.name}
                      </span>
                    </If>
                  </button>
                  <If condition={discoverTopicSelected && !navOpen}>
                    <button
                      className={styles.SearchBarCloseButton}
                      onClick={(() => router.push('/exp/interior-design-ideas'))}
                      aria-label="close"
                      type="button"
                    >
                      <span className="havenly-icon close" />
                    </button>
                  </If>

                  <If condition={navOpen}>
                    <button
                      className={styles.CloseButton}
                      onClick={(() => setNavOpen(false))}
                      type="button"
                    >
                      <span className="havenly-icon close" />
                    </button>
                    <input
                      type="text"
                      placeholder={navSearchPlaceHolderText}
                      className={styles.NavSearchInput}
                      value={navSearchQueryText}
                      onChange={(e) => setNavSearchQueryText(e.target.value)}
                      ref={navSearchQueryInputRef}
                    />
                    <NavSearch queryText={navSearchQueryText} />
                  </If>
                </div>
              </div>
            </div>
          </If>
        </>
      </div>
    </If>
  );
}

function getReadMoreSplitText(textToSplit: string, splitNumber: number) {
  const descriptionWords = textToSplit.split(' ');

  if (descriptionWords.length > splitNumber) {
    const firstPhrase = `${descriptionWords.slice(0, splitNumber).join(' ')}`;
    const secondPhrase = ` ${descriptionWords.slice(splitNumber, descriptionWords.length).join(' ')} `;
    return [boldTopicName(firstPhrase), secondPhrase];
  } else {
    return [false];
  }
}

function boldTopicName(name: string) {
  if (name.includes('%*')) {
    const splitForBold = name.split('%*');
    return (
      <span>
        {splitForBold[0]}
        <span className={styles.boldTopicName}>
          {splitForBold[1]}
        </span>
        {splitForBold[2]}
      </span>
    );
  } else {
    return name;
  }
}

function deslugifyRoom(roomType: IRoomType | undefined) {
  if (roomType?.type && Number(roomType.id) !== OTHER) {
    return roomType.type.toLowerCase();
  }

  return '';
}

function formatSubStyleName(styleType: number) {
  if (styleType) {
    const style = subStylesDisplayNameById[styleType];
    if (style) {
      return style.toLowerCase();
    }
  }

  return '';
}

function getHeader(
  roomType: IRoomType | undefined,
  styleType: number,
  discoverPage?: IDiscoverPage
) {
  if (discoverPage) {
    if (discoverPage.header) {
      return discoverPage.header;
    }

    return `Discover %*${discoverPage.name ? `${discoverPage.name.toLowerCase()}%* ` : ''}design ideas & inspiration, `
      + `expertly curated for you.`;
  }

  const roomName = deslugifyRoom(roomType);
  const subStyleName = formatSubStyleName(styleType);

  return `Discover %*${subStyleName || ''} ${roomName || ''}%* design ideas & inspiration, `
    + `expertly curated for you.`;
}

function getSubHeader(
  roomType: IRoomType | undefined,
  styleType: number,
  discoverPage?: IDiscoverPage
) {
  if (discoverPage) {
    if (discoverPage.description) {
      return discoverPage.description;
    }

    return `Explore ${discoverPage.name ? `${discoverPage.name.toLowerCase()} ` : ''}decor and design ideas, `
      + `save them to inspire your next project, and shop your favorite products.`;
  }

  const roomName = deslugifyRoom(roomType);
  const subStyleName = formatSubStyleName(styleType);

  return `Explore ${subStyleName || ''} ${roomName || ''} decor and design ideas, `
    + `save them to inspire your next project, and shop your favorite products. `;
}
